"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCopy,
  faLink,
  faShareAlt
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

type ShareActionsLayout = "modal" | "stacked" | "compact" | "inline";

type NativeShareOutcome = "completed" | "cancelled" | "failed";

type ShareActionsProps = {
  url: string;
  message: string;
  nativeTitle: string;
  nativeText: string;
  layout?: ShareActionsLayout;
  whatsappLabel?: string;
  nativeLabel?: string;
  copyLabel?: string;
  copiedLabel?: string;
  copyDividerLabel?: string;
  onWhatsAppShared?: () => void;
  onNativeShareOutcome?: (outcome: NativeShareOutcome, error?: unknown) => void;
  onCopySuccess?: () => void;
  onCopyFailure?: (error: unknown) => void;
};

function canUseNativeShare(shareData: ShareData) {
  if (typeof navigator === "undefined") return false;

  const isDevMobilePreview =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("_devpreview") &&
    (new URLSearchParams(window.location.search).has("_devnative") ||
      (typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 767px)").matches));

  if (isDevMobilePreview) return true;

  const isTouchShareSurface =
    navigator.maxTouchPoints > 0 ||
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches);

  if (!isTouchShareSurface) return false;
  if (typeof navigator.share !== "function") return false;
  if (typeof navigator.canShare !== "function") return true;
  return navigator.canShare(shareData);
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}

export default function ShareActions({
  url,
  message,
  nativeTitle,
  nativeText,
  layout = "stacked",
  whatsappLabel = "WhatsApp",
  nativeLabel = "Meer opties",
  copyLabel = "Kopieer",
  copiedLabel = "Gekopieerd",
  copyDividerLabel = "of kopieer link",
  onWhatsAppShared,
  onNativeShareOutcome,
  onCopySuccess,
  onCopyFailure
}: ShareActionsProps) {
  const [copied, setCopied] = useState(false);
  const [nativeShareAvailable, setNativeShareAvailable] = useState(false);
  const whatsappButtonSizerRef = useRef<HTMLDivElement>(null);
  const [stackWidth, setStackWidth] = useState<number>();

  const nativeShareData = useMemo<ShareData>(
    () => ({
      title: nativeTitle,
      text: nativeText,
      url
    }),
    [nativeText, nativeTitle, url]
  );

  useEffect(() => {
    setNativeShareAvailable(canUseNativeShare(nativeShareData));
  }, [nativeShareData]);

  useLayoutEffect(() => {
    if (layout === "inline") return;

    const button = whatsappButtonSizerRef.current;
    if (!button) return;

    const updateWidth = () => {
      setStackWidth(Math.ceil(button.getBoundingClientRect().width));
    };

    updateWidth();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateWidth)
        : null;
    resizeObserver?.observe(button);
    window.addEventListener("resize", updateWidth);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [layout, whatsappLabel]);

  const handleWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    onWhatsAppShared?.();
  };

  const handleNativeShare = async () => {
    if (!nativeShareAvailable) return;

    try {
      await navigator.share(nativeShareData);
      onNativeShareOutcome?.("completed");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        onNativeShareOutcome?.("cancelled", err);
        return;
      }
      onNativeShareOutcome?.("failed", err);
    }
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(url);
      setCopied(true);
      onCopySuccess?.();
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      onCopyFailure?.(err);
    }
  };

  if (layout === "inline") {
    return (
      <div className="flex flex-wrap items-stretch justify-center gap-2">
        <button
          type="button"
          onClick={handleWhatsApp}
          className="flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1fb85a] active:bg-[#18994b]"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="text-lg" />
          {whatsappLabel}
        </button>

        {nativeShareAvailable && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50 active:bg-gray-100"
          >
            <FontAwesomeIcon
              icon={faShareAlt}
              className="text-sm text-gray-500"
            />
            {nativeLabel}
          </button>
        )}

        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 active:bg-gray-200"
        >
          <FontAwesomeIcon
            icon={copied ? faCheck : faLink}
            className="text-xs"
          />
          {copied ? copiedLabel : "Kopieer link"}
        </button>
      </div>
    );
  }

  const stackStyle = stackWidth ? { width: `${stackWidth}px` } : undefined;
  const stackedButtonBaseClass =
    "flex items-center justify-center gap-3 whitespace-nowrap rounded-xl bg-[#25D366] px-4 py-3 text-white transition-colors hover:bg-[#1fb85a] active:bg-[#18994b]";
  const stackedButtonClass = `${stackedButtonBaseClass} w-full`;

  return (
    <>
      <div
        ref={whatsappButtonSizerRef}
        aria-hidden="true"
        className={`${stackedButtonBaseClass} pointer-events-none absolute invisible w-auto`}
      >
        <FontAwesomeIcon icon={faWhatsapp} className="text-2xl" />
        <span className="text-base font-bold">{whatsappLabel}</span>
      </div>

      <div
        className="mx-auto flex max-w-full flex-col items-stretch gap-4"
        style={stackStyle}
      >
        <div className="flex w-full max-w-full flex-col gap-2">
          <button
            type="button"
            onClick={handleWhatsApp}
            className={stackedButtonClass}
          >
            <FontAwesomeIcon icon={faWhatsapp} className="text-2xl" />
            <span className="text-base font-bold">{whatsappLabel}</span>
          </button>

          {nativeShareAvailable && (
            <button
              type="button"
              onClick={handleNativeShare}
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-800 transition-colors hover:bg-gray-50 active:bg-gray-100"
            >
              <FontAwesomeIcon
                icon={faShareAlt}
                className="text-xl text-gray-500"
              />
              <span className="text-base font-bold">{nativeLabel}</span>
            </button>
          )}
        </div>

        <div className="flex w-full items-center gap-3">
          <span className="h-px flex-1 bg-gray-200" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">
            {copyDividerLabel}
          </span>
          <span className="h-px flex-1 bg-gray-200" />
        </div>

        <button
          type="button"
          onClick={handleCopy}
          aria-label="Kopieer link"
          className={`flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors ${
            copied
              ? "border-green-200 bg-green-50"
              : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white active:bg-gray-100"
          }`}
        >
          <FontAwesomeIcon icon={faLink} className="text-sm text-gray-400" />
          <p
            dir="ltr"
            className="min-w-0 flex-1 truncate text-left text-sm text-gray-700"
          >
            {url}
          </p>
          <span
            className={`flex shrink-0 items-center gap-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
              copied ? "text-green-700" : "text-gray-500"
            }`}
          >
            <FontAwesomeIcon
              icon={copied ? faCheck : faCopy}
              className="text-sm"
            />
            {copied ? copiedLabel : copyLabel}
          </span>
        </button>
      </div>
    </>
  );
}
